import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { useDrawer, usePersona } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import { casesWithSession } from '@/versions/v1/modules/advisory/Scene.transform';
import { RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { RoleLandingDashboard } from './RoleLandingDashboard';
import { ControlMetHeaderFact } from './ControlMetLanding';
import { SeniorMetHeaderFacts } from './SeniorMetLanding';
import type { PerformanceInput } from './Performance.transform';
import {
  bunkerHillLoops,
  dashboardForRole,
  defaultPlantRole,
  overviewCopy,
  parseRoleSearchParam,
  pendingFmeaReviewCount,
  personaFromPlantRole,
  plantRoleIdFromPersona,
} from './Scene.transform';

export function OverviewScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { persona, setPersona } = usePersona();
  const { can } = useRole();
  const project = projectTileBySlug(projectId);
  const plantName = project?.name ?? 'this plant';
  const copy = overviewCopy(plantName);
  const { open: openDrawer } = useDrawer();
  const { phase, state } = useScenario();
  const advisory = useAdvisory();

  const roleFromQuery = parseRoleSearchParam(searchParams.get('role'));
  const roleFromPersona = plantRoleIdFromPersona(persona);
  const roleId: PlantRoleId = roleFromQuery ?? roleFromPersona ?? defaultPlantRole();
  const dashboard = dashboardForRole(roleId);
  const pendingReviews = pendingFmeaReviewCount();

  useEffect(() => {
    if (!roleFromQuery) return;
    setPersona(personaFromPlantRole(roleFromQuery));
    setSearchParams({}, { replace: true });
  }, [roleFromQuery, setPersona, setSearchParams]);

  useEffect(() => {
    document.title = project
      ? `${copy.title} · ${project.name}`
      : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  const performanceInput: PerformanceInput = useMemo(
    () => ({
      cases: casesWithSession(state, advisory),
      loops: bunkerHillLoops(),
      actions: RECOMMENDED_ACTIONS,
    }),
    [state, advisory],
  );

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
          <Link to="/projects" className="text-sm font-medium text-accent-700 dark:text-accent-400">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const openCaseDrawer = (caseId: string) => {
    const caseItem = casesWithSession(state, advisory).find((row) => row.id === caseId);
    if (!caseItem) return;
    openDrawer('case-drawer', {
      kicker: caseItem.code ?? 'Case',
      title: caseItem.title,
      wide: true,
      data: { caseId },
    });
  };


  return (
    <SurfacePageLayout width="full">
      <Commentable name="OverviewScene">
        <SurfacePageBody className="space-y-5" width="full">
          <SurfacePageHeader
            title={copy.title}
            titleSize="large"
            actions={
              <>
                {roleId === 'metallurgist' ? <ControlMetHeaderFact state={state} /> : null}
                {roleId === 'senior-metallurgist' ? (
                  <SeniorMetHeaderFacts state={state} advisory={advisory} projectSlug={project.slug} />
                ) : null}
                <HelpAnchor doc="overview/role-landing" label="Overview" />
              </>
            }
          />
          {/* The Senior metallurgist's landing carries its reviews in its strip
              and the Maintenance landing in its "Waiting on you" list, so an
              idle walkthrough leaves this row empty for those personas and it
              is not drawn (an empty row cost the landing 18 px, polish.P27). */}
          {phase !== 'idle' ||
          (can('fmea:review') &&
            pendingReviews > 0 &&
            roleId !== 'senior-metallurgist' &&
            roleId !== 'maintenance') ? (
            <div className="-mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
              {phase !== 'idle' ? (
                <span className="text-xs">
                  Walkthrough control is on{' '}
                  <Link
                    to={projectSidebarHref(project.slug, 'plant-model')}
                    className="font-medium text-accent-700 dark:text-accent-400"
                  >
                    Plant model
                  </Link>
                </span>
              ) : null}
              {can('fmea:review') &&
              pendingReviews > 0 &&
              roleId !== 'metallurgist' &&
              roleId !== 'senior-metallurgist' &&
              roleId !== 'maintenance' ? (
                <span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">
                    {pendingReviews} proposed change{pendingReviews === 1 ? '' : 's'} awaiting your review
                  </span>
                  {' → '}
                  <Link
                    to={`${projectSidebarHref(project.slug, 'advisory')}?tab=approvals`}
                    className="font-medium text-accent-700 dark:text-accent-400"
                  >
                    Approvals
                  </Link>
                </span>
              ) : null}
            </div>
          ) : null}

          <Commentable name="Body">
            <RoleLandingDashboard
              roleId={roleId}
              dashboard={dashboard}
              projectSlug={project.slug}
              performanceInput={performanceInput}
              sessionState={state}
              advisory={advisory}
              onOpenCase={openCaseDrawer}
            />
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
